import type { SkillDefinition } from '../domain/types';

/**
 * Nine permanent Vanguard active skills (VANGUARD_FINAL_SPEC).
 * Unlocked via talent tree; appear in dungeon skill offers only when unlocked.
 * Level tables 1–5 match the confirmed design — combat wiring is M6-B.
 */
export const VANGUARD_PERMANENT_ACTIVES: readonly SkillDefinition[] = [
  {
    id: 'cleave',
    name: 'Cleave',
    description:
      'Next attack +10/20/30/40/50% primary attack damage; splash to every other enemy equals 50/60/70/80/90% of actual final primary-target damage.',
    classId: 'vanguard',
    kind: 'primary_dice',
    category: 'damage',
    maxLevel: 5,
    effect: {
      type: 'vanguard_active',
      activeId: 'cleave',
      primaryDamagePctByLevel: [10, 20, 30, 40, 50],
      splashPctOfPrimaryByLevel: [50, 60, 70, 80, 90],
    },
  },
  {
    id: 'rend',
    name: 'Rend',
    description:
      'Next attack +10/20/30/40/50% attack damage; 1/2/3/4/5 guaranteed Bleed stacks on hit (in addition to ordinary Bleed Chance).',
    classId: 'vanguard',
    kind: 'primary_dice',
    category: 'status',
    maxLevel: 5,
    effect: {
      type: 'vanguard_active',
      activeId: 'rend',
      primaryDamagePctByLevel: [10, 20, 30, 40, 50],
      guaranteedBleedStacksByLevel: [1, 2, 3, 4, 5],
    },
  },
  {
    id: 'bloodlust',
    name: 'Bloodlust',
    description:
      'Next successful attack heals 1/2/3/4/5% of max HP once per activation.',
    classId: 'vanguard',
    kind: 'primary_dice',
    category: 'defence',
    maxLevel: 5,
    effect: {
      type: 'vanguard_active',
      activeId: 'bloodlust',
      healMaxHpPctByLevel: [1, 2, 3, 4, 5],
    },
  },
  {
    id: 'shield_breaker',
    name: 'Shield Breaker',
    description:
      "Strip target's accumulated Shield before damage; next attack +10/20/30/40/50% damage.",
    classId: 'vanguard',
    kind: 'primary_dice',
    category: 'damage',
    maxLevel: 5,
    effect: {
      type: 'vanguard_active',
      activeId: 'shield_breaker',
      primaryDamagePctByLevel: [10, 20, 30, 40, 50],
      stripEnemyShield: true,
    },
  },
  {
    id: 'sunder',
    name: 'Sunder',
    description:
      'Next attack +10/20/30/40/50% damage; consume existing Vulnerable on the hit, then apply 1 new Vulnerable stack.',
    classId: 'vanguard',
    kind: 'primary_dice',
    category: 'status',
    maxLevel: 5,
    effect: {
      type: 'vanguard_active',
      activeId: 'sunder',
      primaryDamagePctByLevel: [10, 20, 30, 40, 50],
      applyVulnerableStacks: 1,
    },
  },
  {
    id: 'flurry',
    name: 'Flurry',
    description:
      'Original attack remains; append 1/2/3/4/5 extra attacks at 60% normal attack damage against a random living enemy.',
    classId: 'vanguard',
    kind: 'primary_dice',
    category: 'damage',
    maxLevel: 5,
    effect: {
      type: 'vanguard_active',
      activeId: 'flurry',
      extraAttacksByLevel: [1, 2, 3, 4, 5],
      extraAttackDamagePct: 60,
    },
  },
  {
    id: 'twin_strike',
    name: 'Twin Strike',
    description:
      'Append exactly one off-hand attack at 100/125/150/175/200% of off-hand weapon damage. Requires a damaging off-hand weapon in the dungeon equipment snapshot to appear in offers.',
    classId: 'vanguard',
    kind: 'primary_dice',
    category: 'damage',
    maxLevel: 5,
    effect: {
      type: 'vanguard_active',
      activeId: 'twin_strike',
      offHandDamagePctByLevel: [100, 125, 150, 175, 200],
    },
    /** M9-A: offer eligibility from entry equipment snapshot. */
    equipmentGate: { requireOffHandWeapon: true },
  },
  {
    id: 'iron_harvest',
    name: 'Iron Harvest',
    description:
      'Generate Shield equal to 2/4/6/8/10% of total damage dealt to all enemies by the activating attack (including Cleave AoE).',
    classId: 'vanguard',
    kind: 'primary_dice',
    category: 'defence',
    maxLevel: 5,
    effect: {
      type: 'vanguard_active',
      activeId: 'iron_harvest',
      shieldFromDamagePctByLevel: [2, 4, 6, 8, 10],
    },
  },
  {
    id: 'kinetic_cascade',
    name: 'Kinetic Cascade',
    description:
      'Separate attack dealing 100/125/150/175/200% of current accumulated Shield as damage; stuns target 1 turn; does not consume Shield.',
    classId: 'vanguard',
    kind: 'primary_dice',
    category: 'damage',
    maxLevel: 5,
    effect: {
      type: 'vanguard_active',
      activeId: 'kinetic_cascade',
      shieldDamagePctByLevel: [100, 125, 150, 175, 200],
      stunTurns: 1,
    },
  },
] as const;

export const VANGUARD_PERMANENT_ACTIVE_IDS: readonly string[] =
  VANGUARD_PERMANENT_ACTIVES.map((s) => s.id);
